import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
// 统一使用已配置了 baseURL 与 Token 拦截器的实例
import axios from '../axiosConfig';

// 创建 AuthContext
const AuthContext = createContext();

// 提供 AuthContext 的 Provider 组件
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null); // 存储当前用户信息
  const [token, setToken] = useState(null); // 存储登录后的 Token
  const [loading, setLoading] = useState(false);
  const [isTokenValid, setIsTokenValid] = useState(true); // 添加token有效性状态

  // 登录函数 - 支持两种登录方式
  const login = async (usernameOrData, password = null) => {
    try {
      setLoading(true);
      
      // 区分两种登录调用方式
      if (typeof usernameOrData === 'object' && usernameOrData !== null) {
        // 直接传入用户对象的方式
        const userData = usernameOrData;
        const userToken = password; // 这里的password实际是token
        
        setUser(userData);
        setToken(userToken);
        setIsTokenValid(true); // 添加这行
        localStorage.setItem('token', userToken);
        storeUserData(userData);
        
        // 触发登录成功事件
        window.dispatchEvent(new CustomEvent('auth:login', { 
          detail: '登录成功' 
        }));
        
        return { success: true };
      } else {
        // 传入用户名和密码的方式
        const response = await axios.post('/auth/login', { 
          username: usernameOrData, 
          password: password 
        });
        
        if (response.data.token) {
          setToken(response.data.token);
          setUser(response.data.user);
          setIsTokenValid(true); // 添加这行
          localStorage.setItem('token', response.data.token);
          storeUserData(response.data.user);
          
          // 触发登录成功事件
          window.dispatchEvent(new CustomEvent('auth:login', { 
            detail: '登录成功' 
          }));
          
          return { success: true };
        } else {
          return { 
            success: false, 
            message: response.data.message || '登录失败，请重试' 
          };
        }
      }
    } catch (error) {
      console.error('Login error:', error);
      return { success: false, message: '登录时发生错误，请重试' };
    } finally {
      setLoading(false);
    }
  };

  // 登出函数
  const logout = () => {
    setToken(null);
    setUser(null);
    setIsTokenValid(false); // 添加这行
    // 使用统一的函数清除用户数据
    clearUserData();
    localStorage.removeItem('token');
    
    // 触发自定义事件，通知其他组件用户已登出
    window.dispatchEvent(new CustomEvent('auth:logout', { 
      detail: '登录已过期，请重新登录' 
    }));
  };

  // 登录成功后存储用户数据的函数
  const storeUserData = (userData) => {
    // 同时存储到两个键名，以保持兼容性
    localStorage.setItem('user', JSON.stringify(userData));
    localStorage.setItem('userData', JSON.stringify(userData));
  };

  // 登出时清除用户数据
  const clearUserData = () => {
    localStorage.removeItem('user');
    localStorage.removeItem('userData');
  };

  // 初始化状态（从 localStorage 加载 Token 和用户信息）
  useEffect(() => {
    const savedToken = localStorage.getItem('token');
    let savedUser = null;
    
    // 首先尝试从userData获取
    const userData = localStorage.getItem('userData');
    if (userData) {
      try {
        savedUser = JSON.parse(userData);
      } catch (e) {
        console.error('Error parsing userData:', e);
        clearUserData(); // 数据损坏，清除数据
      }
    }
    
    // 如果userData不存在或解析失败，尝试从user获取
    if (!savedUser) {
      const userInfo = localStorage.getItem('user');
      if (userInfo) {
        try {
          savedUser = JSON.parse(userInfo);
          // 同时更新到userData，保持一致性
          localStorage.setItem('userData', userInfo);
        } catch (e) {
          console.error('Error parsing user info:', e);
          clearUserData(); // 数据损坏，清除数据
        }
      }
    }

    if (savedToken) {
      // 即使没有用户数据，也设置token（可能是从localStorage恢复的）
      setToken(savedToken);
      
      if (savedUser) {
        setUser(savedUser);
      } else {
        // 如果有token但没有用户数据，尝试通过token获取用户信息
        const fetchUserInfo = async () => {
          try {
            const response = await axios.get('/auth/info', {
              headers: { Token: savedToken }
            });
            
            if (response.data && response.data.code === 0 && response.data.data) {
              setUser(response.data.data);
              storeUserData(response.data.data);
            } else {
              // 如果token无效或过期，清除所有数据
              console.warn('Token无效或已过期');
              logout();
            }
          } catch (error) {
            console.error('获取用户信息失败:', error);
            // 如果请求失败（可能是网络问题），保留token，但不做其他操作
          }
        };
        
        fetchUserInfo();
      }
    } else if (savedUser) {
      // 如果有用户数据但没有token，清除用户数据（防止状态不一致）
      clearUserData();
    }
  }, []);

  // 检查token是否有效的函数
  const validateToken = useCallback(async () => {
    const currentToken = localStorage.getItem('token');
    if (!currentToken) {
      setIsTokenValid(false);
      return false;
    }

    try {
      // 尝试获取用户信息来验证token是否有效
      const response = await axios.get('/yonghu/session', {
        headers: { Token: currentToken }
      });
      
      if (response.data && response.data.code === 0) {
        setIsTokenValid(true);
        // 如果没有用户信息，更新用户信息
        if (!user && response.data.data) {
          setUser(response.data.data);
          storeUserData(response.data.data);
        }
        return true;
      } else {
        // Token无效，自动登出
        console.warn('Token已过期或无效，自动登出');
        logout();
        setIsTokenValid(false);
        return false;
      }
    } catch (error) {
      console.error('Token验证失败:', error);
      
      // 如果是404错误，说明session接口不存在，尝试其他验证方式
      if (error.response && error.response.status === 404) {
        try {
          // 尝试获取用户列表来验证token（这只是一个简单的验证）
          const fallbackResponse = await axios.get('/yonghu/list?page=1&limit=1', {
            headers: { Token: currentToken }
          });
          
          if (fallbackResponse.data && fallbackResponse.data.code === 0) {
            setIsTokenValid(true);
            return true;
          } else {
            console.warn('Token验证失败，自动登出');
            logout();
            setIsTokenValid(false);
            return false;
          }
        } catch (fallbackError) {
          console.error('Fallback token验证也失败:', fallbackError);
          if (fallbackError.response && (fallbackError.response.status === 401 || fallbackError.response.status === 403)) {
            console.warn('Token验证失败，自动登出');
            logout();
            setIsTokenValid(false);
            return false;
          }
          // 其他错误（如网络问题）不自动登出，但标记token为无效
          setIsTokenValid(false);
          return false;
        }
      }
      
      // 网络错误或服务器错误时，假设token可能过期
      if (error.response && (error.response.status === 401 || error.response.status === 403)) {
        console.warn('Token验证失败，自动登出');
        logout();
        setIsTokenValid(false);
        return false;
      }
      // 其他错误（如网络问题）不自动登出，但标记token为无效
      setIsTokenValid(false);
      return false;
    }
  }, [user]);

  // 定期检查token有效性
  useEffect(() => {
    if (!token) return;

    // 立即检查一次
    validateToken();

    // 设置定期检查（每5分钟检查一次）
    const interval = setInterval(() => {
      validateToken();
    }, 5 * 60 * 1000); // 5分钟

    // 清理定时器
    return () => clearInterval(interval);
  }, [token, validateToken]);

  return (
    <AuthContext.Provider value={{ user, token, login, logout, loading, validateToken, isTokenValid }}>
      {children}
    </AuthContext.Provider>
  );
};

// 自定义 Hook：方便在其他组件中使用 AuthContext
export const useAuth = () => {
  return useContext(AuthContext);
};